import { useState, useEffect } from 'react'
import PublicLayout from '@/components/layout/PublicLayout'
import { detectRegion, type Region } from '@/utils/time'
import { REGION_DATA } from '@/components/layout/HealthDropdown'

const ALL_REGIONS: Region[] = ['AU', 'UK', 'US', 'NZ']

export default function Help({ embedded }: { embedded?: boolean }) {
  const [region, setRegion] = useState<Region>(detectRegion)

  useEffect(() => {
    const handler = (e: Event) => setRegion((e as CustomEvent).detail)
    window.addEventListener('ssdl:region-change', handler)
    return () => window.removeEventListener('ssdl:region-change', handler)
  }, [])

  const data = REGION_DATA[region]
  const otherRegions = ALL_REGIONS.filter(r => r !== region)

  const Wrapper = embedded ? ({ children }: { children: React.ReactNode }) => <>{children}</> : PublicLayout

  return (
    <Wrapper>
      <div className="flex-1 flex flex-col items-center p-4 py-8 md:py-12">
        <div className="w-full max-w-3xl space-y-6">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <svg className="w-12 h-12 text-[var(--bac-danger)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
              </svg>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)]">Need Help?</h1>
            <p className="text-[var(--text-secondary)] mt-2">
              You're not alone. Support is available 24/7, free and confidential.
            </p>
          </div>

          {/* Emergency Banner */}
          <div className="card border-2 border-[var(--bac-danger)] bg-[var(--bac-danger)]/10">
            <div className="flex items-center gap-3 mb-2">
              <svg className="w-6 h-6 text-[var(--bac-danger)] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              <h2 className="text-lg font-semibold text-[var(--bac-danger)]">In an emergency, call {data.emergency}</h2>
            </div>
            <p className="text-[var(--text-secondary)] text-sm">
              If you or someone else is in immediate danger, call emergency services right away.
            </p>
          </div>

          {/* Crisis Helplines */}
          <div className="card">
            <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-1">
              Crisis Support — {data.flag} {data.label}
            </h2>
            <p className="text-sm text-[var(--text-secondary)] mb-4">Free, confidential helplines available right now.</p>
            <div className="space-y-3">
              {data.helplines.map(line => (
                <div key={line.name} className="flex items-start gap-3 p-3 rounded-lg bg-[var(--bg-primary)]">
                  <svg className="w-5 h-5 text-[var(--accent-teal)] shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                  </svg>
                  <div>
                    <p className="text-[var(--text-primary)] font-medium">{line.name}</p>
                    <p className="text-[var(--accent-teal)] font-mono font-bold">{line.phone}</p>
                    <p className="text-sm text-[var(--text-secondary)]">{line.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Online Resources */}
          <div className="card">
            <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-1">Online Resources</h2>
            <p className="text-sm text-[var(--text-secondary)] mb-4">Websites and services offering support, tools, and community.</p>
            <div className="space-y-3">
              {data.resources.map(resource => (
                <a
                  key={resource.name}
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 p-3 rounded-lg bg-[var(--bg-primary)] hover:bg-[var(--bg-card)] transition-colors block"
                >
                  <svg className="w-5 h-5 text-[var(--accent-teal)] shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                  </svg>
                  <div>
                    <p className="text-[var(--text-primary)] font-medium">{resource.name}<span className="sr-only"> (opens in new tab)</span></p>
                    <p className="text-sm text-[var(--text-secondary)]">{resource.description}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Talk to a Doctor */}
          <div className="card">
            <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-3">{data.doctorTitle}</h2>
            <p className="text-[var(--text-secondary)] leading-relaxed">{data.doctorText}</p>
            <p className="text-[var(--text-secondary)] leading-relaxed mt-3">
              Walking into an appointment with data makes the conversation easier. SSDL can generate a GP-ready report summarising your drinking patterns, risk level, and history — log in to download one.
            </p>
          </div>

          {/* Other Countries */}
          <div className="card">
            <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Other Countries</h2>
            <div className="space-y-4">
              {otherRegions.map(r => {
                const rd = REGION_DATA[r]
                return (
                  <div key={r} className="p-3 rounded-lg bg-[var(--bg-primary)]">
                    <p className="text-[var(--text-primary)] font-medium mb-2">{rd.flag} {rd.label}</p>
                    <div className="space-y-1">
                      {rd.helplines.map(line => (
                        <p key={line.name} className="text-sm text-[var(--text-secondary)]">
                          <span className="text-[var(--text-primary)]">{line.name}:</span>{' '}
                          <span className="text-[var(--accent-teal)] font-mono">{line.phone}</span>
                        </p>
                      ))}
                      <p className="text-sm text-[var(--text-secondary)]">Emergency: {rd.emergency}</p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Reassurance */}
          <div className="text-center text-sm text-[var(--text-secondary)] space-y-2">
            <p>
              Asking for help is a sign of strength, not weakness. Whatever you're going through, support is available.
            </p>
            <p>
              Change the flag in the navigation bar to see help for your country.
            </p>
          </div>
        </div>
      </div>
    </Wrapper>
  )
}
